/**
 * Tax engine for Spanish autónomo calculator.
 * Pure functions — no React, no UI. Update constants in ./tax-constants.js.
 *
 * The math:
 *   1. Net profit P = revenue − expenses.
 *   2. Social Security: P × (1 − generic deduction) / 12 → pick bracket → minimum
 *      monthly base × SS_RATE × 12. Collaborator base floored at SS_COLLAB_FLOOR_BASE.
 *   3. IRPF: P − SS − gastos difícil justificación = net activity income.
 *      Minus joint reduction if applicable → base liquidable.
 *      Apply state scale + Valencia autonomic scale, subtract scales on the
 *      personal/family minimum, sum both.
 *   4. Two scenarios:
 *      - solo: one person handles everything.
 *      - collab: split P, wife gets W as employment income, both pay SS,
 *        IRPF either joint or two individual filings.
 */

import {
  SS_RATE,
  SS_COLLAB_FLOOR_BASE,
  SS_TRAMOS,
  STATE_SCALE,
  CV_SCALE,
  MIN_PERSONAL,
  CHILD_MIN,
  CV_MIN_FACTOR,
  JOINT_REDUCTION,
  WORK_OTHER_EXPENSES,
  DJ_RATE,
  DJ_CAP,
  FLAT_RATE_MONTH,
} from "./tax-constants.js";

const ssTramoIdx = (m) => {
  for (let i = 0; i < SS_TRAMOS.length; i++) if (m <= SS_TRAMOS[i][0]) return i;
  return SS_TRAMOS.length - 1;
};

/**
 * Detailed Social Security computation. Returns intermediate values for UI display.
 * @param {number} netBeforeGeneric - annual net profit before generic deduction
 * @param {{colaborador?: boolean, flat?: boolean}} opts
 */
export function ssDetail(netBeforeGeneric, { colaborador = false, flat = false } = {}) {
  const g = colaborador ? 0.03 : 0.07;
  const computable = Math.max(0, netBeforeGeneric) * (1 - g);
  const monthly = computable / 12;
  const idx = ssTramoIdx(monthly);
  let base = SS_TRAMOS[idx][1];
  let flooredByCollab = false;
  if (colaborador && base < SS_COLLAB_FLOOR_BASE) {
    base = SS_COLLAB_FLOOR_BASE;
    flooredByCollab = true;
  }
  if (flat) {
    return {
      annual: FLAT_RATE_MONTH * 12,
      monthlyQuota: FLAT_RATE_MONTH,
      flat: true,
      generic: g,
      computable,
      monthly,
      base: null,
      idx,
      flooredByCollab,
    };
  }
  return {
    annual: base * SS_RATE * 12,
    monthlyQuota: base * SS_RATE,
    flat: false,
    generic: g,
    computable,
    monthly,
    base,
    idx,
    flooredByCollab,
  };
}

function applyScale(scale, base) {
  base = Math.max(0, base);
  let tax = 0,
    prev = 0;
  for (const [u, r] of scale) {
    if (base > u) {
      tax += (u - prev) * r;
      prev = u;
    } else {
      tax += (base - prev) * r;
      break;
    }
  }
  return tax;
}

/**
 * Detailed IRPF computation. Returns state and autonomic portions separately.
 */
export function irpfDetail(bl, { kids = 1, childShare = 1 } = {}) {
  const childMin = CHILD_MIN[kids] * childShare;
  const sMin = MIN_PERSONAL + childMin;
  const cMin = (MIN_PERSONAL + childMin) * CV_MIN_FACTOR;
  const state = Math.max(0, applyScale(STATE_SCALE, bl) - applyScale(STATE_SCALE, sMin));
  const cv = Math.max(0, applyScale(CV_SCALE, bl) - applyScale(CV_SCALE, cMin));
  return { total: state + cv, state, cv, sMin, cMin, bl };
}

// Employment-income reduction (reducción por obtención de rendimientos del trabajo).
const workReduction = (rnt) =>
  rnt <= 14852
    ? 7302
    : rnt < 19747.5
    ? Math.max(0, 7302 - 1.75 * (rnt - 14852))
    : 0;

/**
 * Solo autónomo scenario. Spouse (if any) doesn't earn → joint declaration dominates.
 */
export function scenarioSolo(R, E, kids, hasSpouse, flat) {
  const P = R - E;
  const ss = ssDetail(P, { flat });
  const dj = Math.min(DJ_RATE * Math.max(0, P), DJ_CAP);
  const net = Math.max(0, P - ss.annual - dj);
  const joint = hasSpouse;
  const bl = Math.max(0, net - (joint ? JOINT_REDUCTION : 0));
  const tax = irpfDetail(bl, { kids });
  return {
    ss: ss.annual,
    tax: tax.total,
    take: P - ss.annual - tax.total,
    bd: { P, ssTit: ss, dj, netActividad: net, joint, bl, irpf: tax },
  };
}

/**
 * Family-collaborator scenario. Wife receives W as employment income.
 * IRPF computed either jointly or as two individual filings.
 */
export function scenarioCollab(R, E, split, joint, kids, flat) {
  const P = R - E;
  const W = split * P;
  const titularBiz = P - W;
  const ssTit = ssDetail(titularBiz, { flat });
  const ssWife = ssDetail(W, { colaborador: true, flat });
  const ssTotal = ssTit.annual + ssWife.annual;
  const djT = Math.min(DJ_RATE * Math.max(0, titularBiz), DJ_CAP);
  const titularNet = Math.max(0, titularBiz - ssTit.annual - djT);
  const wifePrev = Math.max(0, W - ssWife.annual - WORK_OTHER_EXPENSES);
  const wifeRed = workReduction(wifePrev);
  const wifeNet = Math.max(0, wifePrev - wifeRed);
  let tax, blMain, blWife, irMain, irWife;
  if (joint) {
    blMain = Math.max(0, titularNet + wifeNet - JOINT_REDUCTION);
    irMain = irpfDetail(blMain, { kids });
    tax = irMain.total;
  } else {
    blMain = titularNet;
    blWife = wifeNet;
    irMain = irpfDetail(titularNet, { kids, childShare: 0.5 });
    irWife = irpfDetail(wifeNet, { kids, childShare: 0.5 });
    tax = irMain.total + irWife.total;
  }
  return {
    ss: ssTotal,
    tax,
    take: P - ssTotal - tax,
    W,
    ssWife: ssWife.annual,
    ssTit: ssTit.annual,
    bd: {
      P,
      W,
      titularBiz,
      ssTit,
      ssWife,
      djT,
      titularNet,
      wifePrev,
      wifeRed,
      wifeNet,
      joint,
      blMain,
      blWife,
      irMain,
      irWife,
    },
  };
}
